// iter591 — изнесен от Dashboard.jsx (бързи действия + inline модал за тегло)
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

export default function QuickActionsRow({ isDark, bg, API, user, onUserUpdated, onWeightSaved }) {
  const [showWeight, setShowWeight] = useState(false);
  const currentWeight = user?.profile?.body?.weight || user?.profile?.personal?.weight_kg || "";
  const [weight, setWeight] = useState(currentWeight);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  // iter582 — Sync input value when the modal opens / user prop changes,
  // so re-opening after a save shows the persisted weight (not the stale
  // initial value captured by useState(() => …)).
  useEffect(() => {
    if (showWeight) setWeight(currentWeight);
  }, [showWeight, currentWeight]);

  const saveWeight = async () => {
    const w = parseFloat(weight);
    if (!w || w < 30 || w > 250) {
      toast.error(bg ? "Невалидно тегло (30–250 kg)." : "Invalid weight (30–250 kg).");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, { weight: w });
      setSavedTick(true);
      toast.success(bg ? `Записано: ${w} kg` : `Saved: ${w} kg`);
      setTimeout(() => { setShowWeight(false); setSavedTick(false); }, 900);
      if (onUserUpdated) await onUserUpdated();
      if (onWeightSaved) onWeightSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при запис." : "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const actions = [
    { to: "/dashboard/diary", icon: "📸", label: bg ? "Запишете хранене" : "Log meal", testId: "qa-meal" },
    { onClick: () => setShowWeight(true), icon: "⚖️", label: bg ? "Добавете тегло" : "Add weight", testId: "qa-weight" },
    { to: "/dashboard/diary", icon: "💧", label: bg ? "Добавете вода" : "Add water", testId: "qa-water" },
    { to: "/dashboard/analyses", icon: "🩺", label: bg ? "Качете изследване" : "Upload lab", testId: "qa-lab" },
  ];
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="cc-quick-actions">
        {actions.map(a => {
          const cls = `group flex items-center gap-2.5 px-4 py-3 rounded-2xl border transition-all hover:-translate-y-0.5 hover:shadow-sm text-left ${
            isDark ? "bg-[hsl(222,47%,9%)] border-white/5 hover:border-violet-500/30 text-white" : "bg-white border-gray-200 hover:border-violet-200 text-[#1A1A2E]"
          }`;
          const content = (<>
            <span className="text-xl" aria-hidden="true">{a.icon}</span>
            <span className="text-sm font-semibold truncate">{a.label}</span>
          </>);
          if (a.onClick) {
            return (
              <button key={a.testId} type="button" data-testid={a.testId} onClick={a.onClick} className={cls}>
                {content}
              </button>
            );
          }
          return (
            <Link key={a.testId} to={a.to} data-testid={a.testId} className={cls}>{content}</Link>
          );
        })}
      </div>

      {/* iter580 — Inline weight modal (sync към /user/profile, същият source като HI body tab) */}
      {showWeight && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setShowWeight(false)} data-testid="qa-weight-modal">
          <div
            className={`w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl p-6 ${isDark ? "bg-[hsl(222,47%,12%)] text-white" : "bg-white text-[#1A1A2E]"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl" aria-hidden="true">⚖️</span>
              <h3 className="text-lg font-bold">{bg ? "Колко тежиш сега?" : "Your weight now?"}</h3>
            </div>
            <p className={`text-xs leading-relaxed mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg
                ? "Записваме теглото в твоя профил и веднага обновяваме DIL бюджета и Health Intelligence."
                : "We save it to your profile and instantly refresh DIL budget and Health Intelligence."}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.1"
                min="30"
                max="250"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") saveWeight(); }}
                data-testid="qa-weight-input"
                className={`flex-1 px-4 py-3 rounded-xl text-2xl font-bold tabular-nums border ${
                  isDark ? "bg-white/5 border-white/10 text-white" : "bg-gray-50 border-gray-200 text-[#1A1A2E]"
                }`}
                placeholder="0.0"
              />
              <span className={`text-base font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>kg</span>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={saveWeight}
                disabled={saving || savedTick}
                data-testid="qa-weight-save"
                className={`flex-1 inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${
                  savedTick
                    ? (isDark ? "bg-emerald-500/30 text-emerald-100" : "bg-emerald-200 text-emerald-900")
                    : (isDark ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30" : "bg-emerald-100 text-emerald-900 hover:bg-emerald-200")
                }`}
              >
                {savedTick ? `✓ ${bg ? "Записано" : "Saved"}` : saving ? "…" : (bg ? "Запази" : "Save")}
              </button>
              <button
                type="button"
                onClick={() => setShowWeight(false)}
                className={`px-4 py-2.5 rounded-full text-sm font-semibold ${
                  isDark ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-[#1A1A2E]"
                }`}
              >
                {bg ? "Затвори" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
